import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { passkitAPI } from '../../services/api';
import { logger } from '../../utils/logger';

interface TicketTypeEditDialogProps {
  open: boolean;
  onClose: () => void;
  ticketType: {
    id: string;
    name: string;
    description?: string;
    passkit_template_id: string;  // This is beforeRedeemPassTemplateId
    after_redeem_template_id?: string;
  } | null;
  productionId: string;
  onSuccess: () => void;
}

const TicketTypeEditDialog: React.FC<TicketTypeEditDialogProps> = ({
  open,
  onClose,
  ticketType,
  productionId,
  onSuccess
}) => {
  const [name, setName] = useState('');
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens with ticket type data
  useEffect(() => {
    if (ticketType) {
      setName(ticketType.name || '');
      setTermsAndConditions(ticketType.description || '');
    }
  }, [ticketType]);

  const handleSubmit = async () => {
    if (!ticketType) return;

    // Validation
    if (!name.trim()) {
      setError('Naziv je obavezan');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Call the new update API
      await passkitAPI.updateTicketType(productionId, ticketType.id, {
        name: name.trim(),
        beforeRedeemPassTemplateId: ticketType.passkit_template_id,
        ticketTypeConditions: termsAndConditions.trim() || undefined,
        afterRedeemPassTemplateId: ticketType.after_redeem_template_id
      });

      logger.log('✅ Ticket Type Updated:', {
        id: ticketType.id,
        name: name.trim(),
        ticketTypeConditions: termsAndConditions.trim()
      });

      onSuccess();
      handleClose();
    } catch (err: any) {
      logger.error('Error updating ticket type:', err);
      setError(err.response?.data?.detail || 'Greška pri izmeni ticket type-a');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setTermsAndConditions('');
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Izmeni Ticket Type
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Naziv Ticket Type-a"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={loading}
            sx={{ mb: 2 }}
            helperText="Naziv koji će se prikazivati korisnicima"
          />

          <TextField
            fullWidth
            label="Terms & Conditions"
            value={termsAndConditions}
            onChange={(e) => setTermsAndConditions(e.target.value)}
            multiline
            rows={4}
            disabled={loading}
            helperText="Uslovi korišćenja ticket-a"
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={loading}
        >
          Otkaži
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !name.trim()}
        >
          {loading ? <CircularProgress size={24} /> : 'Sačuvaj'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketTypeEditDialog;
